import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import {
    DateRange,
    DefaultMatCalendarRangeStrategy,
    MAT_DATE_RANGE_SELECTION_STRATEGY,
    MatDatepickerInputEvent,
} from '@angular/material/datepicker';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { DateAdapter } from '@angular/material/core';
import { cloneDeep, isDefined, fiGetLocaleCode } from '@fi-shared/utils';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiDateAdapter, FI_FORMATS } from '../fi-date-adapter';

@Component({
    selector: 'fi-range-calendar',
    template: `
        <mat-form-field appearance="outline">
            <mat-label>{{ 'common.date' | fiAppText }}</mat-label>
            <mat-date-range-input [rangePicker]="picker">
                <input
                    matStartDate
                    [ngModel]="tempDateRange.start"
                    (dateInput)="onStartDateInput($event)"
                    [placeholder]="'common.start_date' | fiAppText"
                />
                <input
                    matEndDate
                    [ngModel]="tempDateRange.end"
                    (dateInput)="onEndDateInput($event)"
                    [placeholder]="'common.end_date' | fiAppText"
                />
            </mat-date-range-input>
            <mat-hint>
                {{ 'date.month_day_year_format' | fiAppText | uppercase }} –
                {{ 'date.month_day_year_format' | fiAppText | uppercase }}
            </mat-hint>
            <mat-datepicker-toggle matIconSuffix [for]="picker" disabled></mat-datepicker-toggle>
            <mat-date-range-picker #picker></mat-date-range-picker>
        </mat-form-field>

        <mat-calendar [selected]="tempDateRange" (selectedChange)="onSelectedChange($event)"> </mat-calendar>

        <div class="fi-calendar-actions-container">
            <button mat-button class="BrandedColor" (click)="setInitialTempDateRange()">
                {{ 'common.cancel' | fiAppText }}
            </button>
            <button mat-button class="BrandedColor" (click)="applyChanges()">{{ 'ok' | fiAppText }}</button>
        </div>
    `,
    styleUrls: ['./fi-range-calendar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-range-calendar' },
    providers: [
        {
            provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
            useClass: DefaultMatCalendarRangeStrategy,
        },
        { provide: DateAdapter, useClass: FiDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: FI_FORMATS },
    ],
    standalone: false,
})
export class FiRangeCalendarComponent implements OnInit, OnChanges {
    @Input() selectedDateRange: DateRange<Date>;
    @Output() selectedDateRangeChange = new EventEmitter<DateRange<Date>>();
    tempDateRange: DateRange<Date>;

    constructor(
        private adapter: DateAdapter<any>,
        private fiUserDataService: FiUserDataService,
    ) {
        const isoCode = fiGetLocaleCode(this.fiUserDataService.applicationLanguage);
        adapter.setLocale(isoCode);
    }

    ngOnInit() {
        this.setInitialTempDateRange();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.selectedDateRange) {
            this.setInitialTempDateRange();
        }
    }

    setInitialTempDateRange() {
        this.tempDateRange = isDefined(this.selectedDateRange)
            ? cloneDeep(this.selectedDateRange)
            : new DateRange<Date>(null, null);
    }

    applyChanges() {
        this.selectedDateRange = cloneDeep(this.tempDateRange);
        this.selectedDateRangeChange.emit(this.selectedDateRange);
    }

    onStartDateInput(event: MatDatepickerInputEvent<Date>) {
        this.tempDateRange = new DateRange(event.value, this.tempDateRange?.end);
    }

    onEndDateInput(event: MatDatepickerInputEvent<Date>) {
        this.tempDateRange = new DateRange(this.tempDateRange?.start, event.value);
    }

    onSelectedChange(date: Date) {
        if (
            this.tempDateRange &&
            this.tempDateRange.start &&
            date.getTime() > this.tempDateRange.start.getTime() &&
            !this.tempDateRange.end
        ) {
            this.tempDateRange = new DateRange(this.tempDateRange.start, date);
        } else {
            this.tempDateRange = new DateRange(date, undefined);
        }
    }
}
